import { create } from 'zustand';
import { DeleteCollege, ListColleges, UploadFile, CreateCollege, EditCollege } from '../api/api';

export interface College {
  _id: string;
  name: string;
  location: string;
  image: string;
  website: string;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GetCollegesResponse {
  data: {
    colleges: {
      data: College[];
      nextId: string | null;
    };
  };
}

interface DeleteCollegeResponse {
  status: boolean;
  message: string;
}

interface CollegeStore {
  colleges: College[];
  getColleges: (search: string, nextId: string | null, append: boolean, limit: number) => Promise<void>;
  nextId: string | null;
  deleteCollege: (id: string) => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  uploadedFile: UploadFileResponse | null;
  addCollege: (college: CreateCollegeI) => Promise<void>;
  editCollege: (college: EditCollegeI) => Promise<void>;
}

interface EditCollegeI {
  name: string | null;
  location: string | null;
  website: string | null;
  imageId: string | null;
  _id: string;
  image: string | null;
}

interface UploadFileResponse {
  data: {
    upload: {
      uploadUrl: string;
      isDeleted: boolean;
      _id: string;
      createdAt: string;
      updatedAt: string;
      __v: number;
    };
  };
  status: boolean;
  message: string;
}

interface CreateCollegeResponse {
  status: boolean;
  message: string;
  data: {
    college: College;
  };
}

interface CreateCollegeI {
  name: string;
  location: string;
  website: string;
  imageId: string;
}

export const useCollegeStore = create<CollegeStore>((set, get) => ({
  colleges: [] as College[],
  nextId: null,
  uploadedFile: null,
  getColleges: async (search: string, nextId: string | null, append: boolean, limit: number) => {
    const { data, error } = await ListColleges<GetCollegesResponse>(search, nextId, limit);

    if (error) {
      return;
    }

    if (append) {
      set({ colleges: [...get().colleges, ...data.data.colleges.data] });
    } else {
      set({ colleges: data.data.colleges.data });
    }
    set({ nextId: data.data.colleges.nextId });
  },

  deleteCollege: async (id: string) => {
    const { data, error } = await DeleteCollege<DeleteCollegeResponse>(id);

    if (error || !data.status) {
      return;
    }

    const colleges = get().colleges.filter((college) => college._id !== id);
    set({ colleges });
  },

  uploadFile: async (file: File) => {
    const { data, error } = await UploadFile<UploadFileResponse | null>(file);

    if (error) {
      return;
    }

    set({ uploadedFile: data });
  },

  addCollege: async (college: CreateCollegeI) => {
    const { data, error } = await CreateCollege<CreateCollegeResponse>(college);

    if (error || !data.status) {
      return;
    }

    const colleges = get().colleges;
    set({ colleges: [...colleges, data.data.college] });
  },

  editCollege: async (college: EditCollegeI) => {
    const { data, error } = await EditCollege<DeleteCollegeResponse>(college);

    if (error || !data.status) {
      return;
    }

    // Get current state
    const currentColleges = get().colleges;

    // Create a new array with the updated college
    const updatedColleges = currentColleges.map((c) =>
      c._id === college._id
        ? {
            ...c,
            name: college.name || c.name,
            location: college.location || c.location,
            website: college.website || c.website,
            image: college.image || c.image,
          }
        : c
    );

    // Set the new state with the entire new array
    set({ colleges: updatedColleges });
  },
}));
